import { useEffect } from 'react';

/**
 * usePageMeta — lightweight per-page <title> + <meta description> + canonical
 * + OG/Twitter card setter for an SPA.
 *
 * No new dependencies (we deliberately avoid react-helmet/-async). Updates
 * document.title, the existing <meta name="description">, the canonical link,
 * and the OG/Twitter tags on mount; restores everything to its prior value on
 * unmount so navigating between routes never leaves stale meta on the page.
 *
 * Why this matters for SEO + AI discoverability:
 *   - Google's crawler executes JS and re-reads document.title, meta, and
 *     canonical after render — per-route values eliminate duplicate-content
 *     splits across SPA routes.
 *   - Per-route canonicals are how Google decides which URL is "the" URL —
 *     critical for AI-search engines (SearchGPT, Perplexity, You.com) which
 *     follow canonical signals to choose what to ingest.
 *   - Per-route OG/Twitter tags fix the long-standing SPA problem where
 *     LinkedIn, Slack, Facebook, and X always show the static homepage card
 *     no matter which route a user shares. JS-disabled crawlers (LinkedInBot,
 *     Slackbot) still see the static fallback in public/index.html; JS-enabled
 *     ones (most modern social previews) pick up the per-route tags.
 *
 * Restore-on-unmount semantics:
 *   - We snapshot every attribute we touch BEFORE we mutate it, then restore
 *     the snapshot on unmount. This is important for two reasons:
 *       1. Avoiding stale meta when a user navigates between routes (a route
 *          that doesn't pass an `ogImage` shouldn't inherit the previous
 *          route's `ogImage`).
 *       2. Defence-in-depth against tools/tests that mount usePageMeta in
 *          isolation and expect the document to be returned to a clean state.
 *
 * SECURITY:
 *   - Values are passed through the browser's native attribute setter, which
 *     correctly escapes the value for an attribute context. We do not use
 *     innerHTML, dangerouslySetInnerHTML, or document.write. Even so, callers
 *     should pass plain strings (never user-controlled HTML) — these helpers
 *     are intended for static, hard-coded marketing copy from the codebase.
 *   - canonical/ogUrl values are validated to be absolute https:// URLs on
 *     getsmarthub.co. Anything else is rejected (no-op) to prevent a future
 *     bug from accidentally pointing canonical somewhere else and leaking
 *     ranking authority.
 *
 * @param {object} opts
 * @param {string}  opts.title              — full <title> text (recommended ≤ 60 chars)
 * @param {string} [opts.description]       — meta description text (recommended ≤ 160 chars)
 * @param {string} [opts.canonical]         — absolute https://getsmarthub.co/... canonical URL
 * @param {string} [opts.ogTitle]           — OG title; defaults to `title` if omitted
 * @param {string} [opts.ogDescription]     — OG description; defaults to `description` if omitted
 * @param {string} [opts.ogUrl]             — OG URL; defaults to `canonical` if omitted
 * @param {string} [opts.ogImage]           — absolute URL for og:image (recommended 1200×630 jpg/png)
 * @param {string} [opts.ogImageAlt]        — descriptive alt text for og:image
 * @param {string} [opts.twitterTitle]      — Twitter title; defaults to ogTitle/title
 * @param {string} [opts.twitterDescription]— Twitter description; defaults to ogDescription/description
 * @param {string} [opts.twitterImage]      — Twitter image; defaults to ogImage
 */
export function usePageMeta({
  title,
  description,
  canonical,
  ogTitle,
  ogDescription,
  ogUrl,
  ogImage,
  ogImageAlt,
  twitterTitle,
  twitterDescription,
  twitterImage,
} = {}) {
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    /* ───────── Snapshot every value before mutation (restore on unmount) ───────── */
    const snapshot = { title: document.title };
    const restorers = [];

    /**
     * Update an existing meta tag (selected by `selector`); if it doesn't
     * exist, insert one. Returns a restorer that puts the DOM back exactly as
     * it was on unmount.
     *
     * Why this matters: snapshotting + a no-op restorer when nothing changes
     * means double-mounting (StrictMode) doesn't accumulate empty meta tags.
     */
    const setMetaTag = ({ selector, attr, attrName, attrValue, content }) => {
      let tag = document.querySelector(selector);
      const created = !tag;
      let previousContent = null;
      if (tag) {
        previousContent = tag.getAttribute('content');
        tag.setAttribute('content', content);
      } else {
        tag = document.createElement('meta');
        tag.setAttribute(attrName, attrValue);
        tag.setAttribute('content', content);
        // Set a marker so the restorer knows we own this tag and can remove it.
        tag.setAttribute('data-smarthub-meta', attr);
        document.head.appendChild(tag);
      }
      restorers.push(() => {
        if (created) {
          tag.parentNode?.removeChild(tag);
        } else if (previousContent !== null) {
          tag.setAttribute('content', previousContent);
        } else {
          tag.removeAttribute('content');
        }
      });
    };

    /* ───────── canonical / ogUrl URL validation ───────── */
    // Reject anything that isn't an absolute https:// URL on getsmarthub.co.
    // Defence-in-depth so a future caller can't accidentally point canonical
    // somewhere else and leak ranking authority.
    const isValidSiteUrl = (url) => {
      if (typeof url !== 'string' || !url) return false;
      try {
        const u = new URL(url);
        return u.protocol === 'https:' && u.hostname === 'getsmarthub.co';
      } catch {
        return false;
      }
    };

    /* ───────── <title> ───────── */
    if (title) {
      document.title = title;
      restorers.push(() => { document.title = snapshot.title; });
    }

    /* ───────── <meta name="description"> ───────── */
    if (description) {
      setMetaTag({
        selector: 'meta[name="description"]',
        attr: 'description',
        attrName: 'name',
        attrValue: 'description',
        content: description,
      });
    }

    /* ───────── <link rel="canonical"> ───────── */
    if (isValidSiteUrl(canonical)) {
      let link = document.querySelector('link[rel="canonical"]');
      const linkCreated = !link;
      let prevHref = null;
      if (link) {
        prevHref = link.getAttribute('href');
        link.setAttribute('href', canonical);
      } else {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        link.setAttribute('href', canonical);
        link.setAttribute('data-smarthub-meta', 'canonical');
        document.head.appendChild(link);
      }
      restorers.push(() => {
        if (linkCreated) {
          link.parentNode?.removeChild(link);
        } else if (prevHref !== null) {
          link.setAttribute('href', prevHref);
        } else {
          link.removeAttribute('href');
        }
      });
    }

    /* ───────── OpenGraph: og:title, og:description, og:url, og:image, og:image:alt ───────── */
    const effectiveOgTitle = ogTitle ?? title;
    const effectiveOgDescription = ogDescription ?? description;
    const effectiveOgUrl = ogUrl ?? canonical;

    if (effectiveOgTitle) {
      setMetaTag({
        selector: 'meta[property="og:title"]',
        attr: 'og:title',
        attrName: 'property',
        attrValue: 'og:title',
        content: effectiveOgTitle,
      });
    }
    if (effectiveOgDescription) {
      setMetaTag({
        selector: 'meta[property="og:description"]',
        attr: 'og:description',
        attrName: 'property',
        attrValue: 'og:description',
        content: effectiveOgDescription,
      });
    }
    if (isValidSiteUrl(effectiveOgUrl)) {
      setMetaTag({
        selector: 'meta[property="og:url"]',
        attr: 'og:url',
        attrName: 'property',
        attrValue: 'og:url',
        content: effectiveOgUrl,
      });
    }
    if (typeof ogImage === 'string' && /^https:\/\//i.test(ogImage)) {
      setMetaTag({
        selector: 'meta[property="og:image"]',
        attr: 'og:image',
        attrName: 'property',
        attrValue: 'og:image',
        content: ogImage,
      });
      if (ogImageAlt) {
        setMetaTag({
          selector: 'meta[property="og:image:alt"]',
          attr: 'og:image:alt',
          attrName: 'property',
          attrValue: 'og:image:alt',
          content: ogImageAlt,
        });
      }
    }

    /* ───────── Twitter card: title, description, image ───────── */
    const effectiveTwitterTitle = twitterTitle ?? effectiveOgTitle;
    const effectiveTwitterDescription = twitterDescription ?? effectiveOgDescription;
    const effectiveTwitterImage = twitterImage ?? ogImage;

    if (effectiveTwitterTitle) {
      setMetaTag({
        selector: 'meta[name="twitter:title"]',
        attr: 'twitter:title',
        attrName: 'name',
        attrValue: 'twitter:title',
        content: effectiveTwitterTitle,
      });
    }
    if (effectiveTwitterDescription) {
      setMetaTag({
        selector: 'meta[name="twitter:description"]',
        attr: 'twitter:description',
        attrName: 'name',
        attrValue: 'twitter:description',
        content: effectiveTwitterDescription,
      });
    }
    if (typeof effectiveTwitterImage === 'string' && /^https:\/\//i.test(effectiveTwitterImage)) {
      setMetaTag({
        selector: 'meta[name="twitter:image"]',
        attr: 'twitter:image',
        attrName: 'name',
        attrValue: 'twitter:image',
        content: effectiveTwitterImage,
      });
    }

    return () => {
      // LIFO restore so created-then-modified tags are correctly torn down.
      for (let i = restorers.length - 1; i >= 0; i -= 1) {
        try { restorers[i](); } catch { /* swallow — best-effort cleanup */ }
      }
    };
  }, [
    title,
    description,
    canonical,
    ogTitle,
    ogDescription,
    ogUrl,
    ogImage,
    ogImageAlt,
    twitterTitle,
    twitterDescription,
    twitterImage,
  ]);
}
